/** The public calculators' units. The editor never speaks these: its lengths
 *  go through room/shared/DimensionField in its own `Units`. */
export type LengthUnit = 'm' | 'ft';

export const M_TO_FT = 3.28084;
export const FT_TO_M = 1 / M_TO_FT;

export function formatLength(meters: number, unit: LengthUnit, digits = 2): string {
  if (unit === 'ft') return `${(meters * M_TO_FT).toFixed(digits)} ft`;
  return `${meters.toFixed(digits)} m`;
}

export function displayFromMeters(meters: number, unit: LengthUnit): number {
  return unit === 'ft' ? meters * M_TO_FT : meters;
}

export function metersFromDisplay(value: number, unit: LengthUnit): number {
  return unit === 'ft' ? value * FT_TO_M : value;
}

/** Readout string: at most 2 decimals with trailing zeros stripped ("5",
 *  "4.5", "3.28") — the IDENTICAL rule in metric and imperial.
 *
 *  Display-only: the stored meter value is never quantized here, so switching
 *  units (1 m → "3.28" ft → back) round-trips exactly and typed input keeps
 *  full precision. Only slider drags snap, via `snapLengthToStep`.
 *
 *  Rounded to 2 rather than truly truncated: float dust such as 2.9999999996
 *  (exactly 3 ft through a meter round-trip) must read "3", where truncation
 *  would print "2.99". */
export function displayLengthEditString(meters: number, unit: LengthUnit): string {
  const n = displayFromMeters(meters, unit);
  const raw = n.toFixed(2);
  return raw.replace(/\.?0+$/, '') || '0';
}

/** Snap a meter value to `step` increments measured in the DISPLAYED unit —
 *  0.1 means 0.1 m in metric and 0.1 ft in imperial — so both unit systems
 *  get the same slider feel. Returns meters at full float precision; the
 *  readout's 2-decimal rule is purely presentational on top of this.
 *  `step <= 0` disables snapping (clamping still applies at the call site). */
export function snapLengthToStep(meters: number, step: number, unit: LengthUnit): number {
  if (!(step > 0)) return meters;
  if (unit === 'ft') {
    const snappedFt = Math.round(displayFromMeters(meters, 'ft') / step) * step;
    return metersFromDisplay(snappedFt, 'ft');
  }
  return Math.round(meters / step) * step;
}
